//dependencies
import { useEffect, useRef } from "react";
import z from "zod";

//hooks
import { usePageState, useUpdatePageState } from "../../api/hooks";

import useSearchQueryParams from "../useSearchQueryParams";

type UsePageStateOptions = {
	defaultMerge?: boolean;
	defaultDebounce?: number;
};

const DEFAULT_OPTIONS: UsePageStateOptions = {
	defaultMerge: true,
	defaultDebounce: 500,
};

type QueryChangeOptions = {
	merge?: boolean;
	replace?: boolean;
	save?: boolean;
};

type UseSavedPageStateProps<T> = {
	pageName: string;
	schema: T;
	defaultOptions?: UsePageStateOptions;
};

/*
    * This hook is used to manage the state of a page and save it to the server. It uses useSearchQueryParams to manage the state of the page in the URL and usePageState and useUpdatePageState to save the state to the server.
    * @param pageName - the name of the page
    * @param schema - the schema of the state
    * @param defaultOptions - the default options for updating the state
    

    This handles the pages state in a heirarchy so that default state from schema < url params < saved state from server
    It also debounces the state updates to the server
*/

const useSavedPageState = <T extends z.ZodObject<z.ZodRawShape>>({
	pageName,
	schema,
	defaultOptions = DEFAULT_OPTIONS,
}: UseSavedPageStateProps<T>) => {
	type StateType = z.infer<T>;
	const { data: savedPageState, isLoading: isSavedPageStateLoading } = usePageState(pageName);
	const { mutate: updatePageState, isPending: isPageStateMutating } = useUpdatePageState<StateType>(pageName);
	const { queryParams, handleQueryChange } = useSearchQueryParams({ schema });
	const timeout = useRef<any | null>(null);

	useEffect(() => {
		if (!isSavedPageStateLoading) {
			if (savedPageState && Object.keys(savedPageState).length > 0) {
				handleQueryChange(savedPageState, { replace: true });
			}
		}
	}, [isSavedPageStateLoading]);

	const DefaultMergeOptions: QueryChangeOptions = {
		merge: !!defaultOptions.defaultMerge,
		replace: true,
		save: true,
	};

	const handleUpdatePageState = (state: StateType, options = DefaultMergeOptions) => {
		options = { ...DefaultMergeOptions, ...options };
		handleQueryChange({ ...state }, { replace: !!options.replace });

		if (timeout.current) {
			clearTimeout(timeout.current);
		}

		timeout.current = setTimeout(() => {
			const saveState = options.merge ? { ...savedPageState, ...state } : state;
			options.save && updatePageState({ state: saveState, merge: !!options.merge });
		}, defaultOptions.defaultDebounce);
	};

	return {
		pageState: queryParams,
		savedPageState,
		isSavedPageStateLoading: isSavedPageStateLoading,
		updatePageState: handleUpdatePageState,
		isPageStateMutating,
	};
};

export default useSavedPageState;
